import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Bookmark, CalendarRange, Check, Clock3, Compass, LoaderCircle, MapPin, Search, Sparkles, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { LocationCombobox } from "../../components/LocationCombobox";
import { NewTripWizard } from "../../components/NewTripWizard";
import { api } from "../../lib/api";
import type { PlaceSuggestion, WishlistItem } from "../../lib/types";
import { SuggestedPlans } from "./SuggestedPlans";

const moods = [
  { label: "Slow & soulful", place: "Kyoto, Japan", note: "Tea rooms, temple paths, unhurried mornings", tone: "sage" },
  { label: "Sun after dark", place: "Lisbon, Portugal", note: "Tiled streets, ocean light, late suppers", tone: "sun" },
  { label: "Wild reset", place: "Queenstown, New Zealand", note: "Alpine air, long trails, clear horizons", tone: "alpine" },
  { label: "Colour & spice", place: "Marrakesh, Morocco", note: "Courtyards, souks, desert-edge wonder", tone: "rose" },
] as const;

const seasons = ["Any season", "Spring", "Summer", "Autumn", "Winter"];
const priorities = [
  ["must_go", "Must go"],
  ["high", "High"],
  ["medium", "Someday"],
  ["low", "Curious"],
] as const;

export function ExplorePage() {
  const queryClient = useQueryClient();
  const [place, setPlace] = useState<PlaceSuggestion | null>(null);
  const [season, setSeason] = useState("Any season");
  const [days, setDays] = useState(5);
  const [why, setWhy] = useState("");
  const [priority, setPriority] = useState<WishlistItem["priority"]>("medium");
  const [wizard, setWizard] = useState(false);
  const [finding, setFinding] = useState<string | null>(null);
  const { data: saved = [], isLoading, isError, refetch } = useQuery({
    queryKey: ["wishlist"],
    queryFn: () => api<WishlistItem[]>("/wishlist"),
  });
  const alreadySaved = useMemo(() => place ? saved.find((item) => item.destination === place.display_name) : undefined, [place, saved]);

  const save = useMutation({
    mutationFn: () => api<WishlistItem>("/wishlist", { method: "POST", body: JSON.stringify({
      destination: place?.display_name,
      why: why.trim() || null,
      ideal_season: season === "Any season" ? null : season,
      duration_days: days,
      priority,
      currency: "INR",
    }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wishlist"] }),
  });
  const update = useMutation({
    mutationFn: ({ id, next }: { id: string; next: WishlistItem["priority"] }) => api<WishlistItem>(`/wishlist/${id}`, { method: "PATCH", body: JSON.stringify({ priority: next }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wishlist"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/wishlist/${id}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wishlist"] }),
  });

  const findPlace = async (query: string) => {
    setFinding(query);
    try {
      const result = await api<PlaceSuggestion[]>(`/places/suggest?q=${encodeURIComponent(query)}&limit=1`);
      if (result[0]) {
        setPlace(result[0]);
        setWhy("");
        window.setTimeout(() => document.getElementById("destination-canvas")?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
      }
    } finally {
      setFinding(null);
    }
  };

  return <section className="explore-page">
    <div className="explore-hero page-width">
      <div className="explore-copy">
        <p className="eyebrow">THE LIVING ATLAS</p>
        <h1>Follow the feeling,<br />then find the place.</h1>
        <p>Search the world with verified map results. Keep the places that pull at you, and turn one into a mapped journey whenever it feels right.</p>
        <div className="explore-search"><Compass size={22} /><LocationCombobox value={place} inputLabel="Search destinations to explore" onSelect={(next) => { setPlace(next); setWhy(""); }} /></div>
        <div className="explore-prompt"><span>TRY A FEELING</span>{moods.slice(0, 3).map((mood) => <button key={mood.label} onClick={() => void findPlace(mood.place)}>{mood.label}</button>)}</div>
      </div>
      <div className="atlas-orbit" aria-hidden="true">
        <span className="orbit orbit-one" /><span className="orbit orbit-two" />
        <span className="atlas-core"><Compass /></span>
        <span className="atlas-label atlas-label-one">35.0° N<br /><b>Kyoto</b></span>
        <span className="atlas-label atlas-label-two">38.7° N<br /><b>Lisbon</b></span>
        <span className="atlas-label atlas-label-three">45.0° S<br /><b>Queenstown</b></span>
      </div>
    </div>

    <div className="mood-deck page-width" aria-label="Explore by mood">
      {moods.map((mood, index) => <motion.button whileHover={{ y: -5 }} className={`mood-card ${mood.tone}`} key={mood.label} onClick={() => void findPlace(mood.place)}>
        <span>0{index + 1}</span><div><small>{mood.place}</small><h2>{mood.label}</h2><p>{mood.note}</p></div>{finding === mood.place ? <LoaderCircle className="location-spinner" /> : <ArrowRight />}
      </motion.button>)}
    </div>

    <SuggestedPlans />

    <AnimatePresence>
      {place && <motion.section id="destination-canvas" className="destination-canvas page-width" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
        <div className="destination-visual">
          {place.image_url ? <img src={place.image_url} alt={`View of ${place.name}`} /> : <div className="destination-map-art"><MapPin /><span>{Number(place.latitude).toFixed(2)}° · {Number(place.longitude).toFixed(2)}°</span></div>}
          <div className="destination-shade" />
          <button className="canvas-close" onClick={() => setPlace(null)} aria-label="Close destination"><X /></button>
          <div className="destination-title"><span>VERIFIED PLACE · {place.provider_id.startsWith("google:") ? "GOOGLE PLACES" : "MAP DATA"}</span><h2>{place.name}</h2><p>{place.admin1 || place.country || place.display_name}</p></div>
        </div>
        <div className="destination-notebook">
          <p className="eyebrow">SHAPE THE POSSIBILITY</p><h2>What could this trip feel like?</h2>
          <label>Best-fit season<select value={season} onChange={(event) => setSeason(event.target.value)}>{seasons.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label>How long?<div className="duration-stepper"><button onClick={() => setDays((value) => Math.max(1, value - 1))}>−</button><strong>{days}<small> days</small></strong><button onClick={() => setDays((value) => Math.min(365, value + 1))}>+</button></div></label>
          <label>Why this place?<textarea rows={3} value={why} onChange={(event) => setWhy(event.target.value)} placeholder="The food, the quiet streets, a promise to yourself…" /></label>
          <div className="priority-pills" aria-label="Priority">{priorities.map(([value, label]) => <button key={value} className={priority === value ? "active" : ""} onClick={() => setPriority(value)}>{label}</button>)}</div>
          {save.error && <p className="form-error">{save.error.message}</p>}
          <div className="canvas-actions"><button className="button soft" disabled={!!alreadySaved || save.isPending} onClick={() => save.mutate()}>{alreadySaved ? <><Check size={16} />Saved to your atlas</> : <><Bookmark size={16} />Save for someday</>}</button><button className="button primary" onClick={() => setWizard(true)}>Plan this journey<ArrowRight size={16} /></button></div>
        </div>
      </motion.section>}
    </AnimatePresence>

    <section className="saved-atlas page-width">
      <header><div><p className="eyebrow">YOUR PRIVATE SHORTLIST</p><h2>Places worth returning to.</h2><p>A lightweight shelf for ideas—no dates, no pressure, until you are ready.</p></div><span><Bookmark size={17} />{saved.length} saved</span></header>
      {isError ? <div className="error-state">We couldn’t open your saved atlas. <button onClick={() => refetch()}>Try again</button></div> : isLoading ? <div className="atlas-loading skeleton" /> : saved.length ? <div className="saved-place-grid">{saved.map((item, index) => <article className={`saved-place saved-tone-${index % 4}`} key={item.id}>
        <div className="saved-place-mark"><span>{String(index + 1).padStart(2, "0")}</span><MapPin /></div>
        <div><small>{item.priority.replace("_", " ")}</small><h3>{item.destination.split(",")[0]}</h3><p>{item.why || "A place to keep in sight."}</p><div className="saved-meta"><span><CalendarRange />{item.ideal_season || "Any season"}</span><span><Clock3 />{item.duration_days || "—"} days</span></div></div>
        <div className="saved-place-actions"><select aria-label={`Priority for ${item.destination}`} value={item.priority} onChange={(event) => update.mutate({ id: item.id, next: event.target.value as WishlistItem["priority"] })}>{priorities.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select><button aria-label={`Remove ${item.destination}`} onClick={() => remove.mutate(item.id)}><Trash2 /></button></div>
      </article>)}</div> : <div className="atlas-empty"><Sparkles /><div><h3>Your atlas is wonderfully empty.</h3><p>Search a destination or choose a feeling above. Your first saved place will land here.</p></div><button className="button ghost" onClick={() => void findPlace("Kyoto, Japan")}>Surprise me<Search size={15} /></button></div>}
    </section>
    {wizard && <NewTripWizard initialDestination={place} onClose={() => setWizard(false)} />}
  </section>;
}
